/*
 * 文件名：labelTaskApi.js
 * 描述：数据标注任务链数据层（v26.3.3，F9 审核列表 / F6 待整改列表 / labelToolDetail / labelExecution 共用）。
 *
 * 契约口径（2026-09-11 用户拍板）：数据接口由 FBT 后端自建（F0，/label-task 系列，sales），
 *   数据结构照搬 fe-csbr-data-labeling 的对应接口（ms-data-label-service 的 VO）：
 *   - 查询体：{pageIndex, pageSize, projectName, labelTypeCode, currentStageCode, stageCode, ...}
 *     stageCode=角色视角（LABEL 标注/REVIEW 审核/QC 质检/ACCEPTANCE 验收）
 *   - 行数据：LabelTaskPackageRSVO 结构（以 09-11 用户提供的 label-manager-test 真实接口
 *     /label-task-package/task-page-list 响应为基准，见本文件 mock 数据）
 * 前端按本层结构先行开发（mock 可测）；FBT 后端同构接口就绪后只改 CONTRACT_URLS 一处。
 *
 * mock 数据形态：对齐真实接口响应（records 字段全集 + totalRows 分页包装）。
 */

// ---- 契约常量：F0 就绪后只改这里 ----
'use strict';

export const CONTRACT_URLS = {
  // 我的任务分页列表（stageCode 视角 + 业务过滤参数），对应 fe task-page-list
  taskPageList: "/label-task/task-page-list",
  // 标注页地址（LS 项目页），执行/详情两处共用
  labelUrl: "/label-task/label-url",
  // 驳回信息摘要（审核记录：驳回时间/驳回人/原因/当前条目/整改汇总），对应 fe label-review-record
  rejectInfo: "/label-task/reject-info",
  // 执行人员候选列表（协配→下发）
  assignCandidates: "/label-task/assign-candidates",
  // 标注结果导出（异步 5000 条/文件）
  exportResult: "/label-task/export-result",
  // ---- N7 行级化三新接口（2026-09-15 批次1后端就绪）----
  // 人员明细分页（按标注明细行，行=执行人维度含三类人员列）
  itemPageList: "/label-task/item-page-list",
  // 三类人员保存（质检逐行必填/初审复审行间联动/自审禁止）
  itemAssign: "/label-task/item-assign",
  // 去派发预检（只读，canDispatch=false 时按钮置灰+逐行原因提示）
  checkDispatch: "/label-task/check-dispatch",
};

// N7 行级化：标注明细行的任务内容类型标识（tr_task_detail.taskContentType，行级路由开关）
export const SJBZ_CONTENT_TYPE = "SJBZ";

// 是否启用本地 mock（F0 接口已就绪[2026-09-14 sales /label-task 全量实现]，置 false 走真实接口；
// mock 函数保留作联调回切开关）
export const MOCK_ENABLED = false;

// ---- fe 口径常量 ----
export const STAGE_CODES = { LABEL: "LABEL", REVIEW: "REVIEW", QC: "QC", ACCEPTANCE: "ACCEPTANCE" };
// 任务包级阶段码：后端 tr_label_task.current_stage_code 直透无转换，实际值域=FBT 码
// LABEL/FIRST_REVIEW/SECOND_REVIEW/QC/COMPLETED（VO 注释里的 fe 老码 REVIEW/ACCEPTANCE 已过时，勿信）
export const PKG_STAGE = { COMPLETED: "COMPLETED" };
// 标注类型（对齐 fe projectLabelTypeList）
export const LABEL_TYPE_LIST = [
  { label: "图像", value: "IMAGE" },
  { label: "文本", value: "TEXT" },
  { label: "音频", value: "AUDIO" },
  { label: "视频", value: "VIDEO" },
];
// LS 实例基址（对齐 fe dataLabelSetting buildLink 的正式域；localhost 环境 fe 用 127.0.0.1:8010，FBT 统一走测试实例）
export const LS_BASE_URL = localStorage.getItem("ls-base-override") || "https://label-test.zgsjzc.com";

/**
 * 组装 fe 口径查询体（task-page-list 入参形态）。
 * @param {Object} query 页内查询条件（projectName/labelTypeCode/state/currentStageCode/pageIndex/pageSize）
 * @param {string} stageCode 角色视角（STAGE_CODES）
 * @param {Object} extra 业务扩展过滤（如待整改页的 rejectedFlag，F0 契约补充位）
 */
export function buildTaskPageQuery(query, stageCode, extra) {
  // 环节筛选（currentStageCode，服务端= 精确过滤）与状态筛选（stageState=ongoing/finished 按视角解释）互斥：
  // 同选时两条件 AND 叠加（如"待质检且已完成"恒空），环节更具体，有环节时状态置空
  const stageFilter = query.currentStageCode || null;
  return {
    pageIndex: query.pageIndex,
    pageSize: query.pageSize,
    projectName: query.projectName || "",
    taskPackageName: query.taskPackageName || "",
    labelTypeCode: query.labelTypeCode || "",
    stageState: stageFilter ? null : (query.state || ""),
    currentStageCode: stageFilter,
    stageCode,
    // 网关 X-USERINFO 不含租户，后端 task-page-list 租户改为 body.tenantGuid 兜底（同 label-dataset 口径）；
    // 主前端 postJson 封装（new-axios）不注入租户，须在此显式携带当前登录租户
    tenantGuid: localStorage.getItem("guid") || "",
    ...(extra || {}),
  };
}

/**
 * fe 任务包行 → FBT 页面行的展示态计算（对齐 task-manage 的角色化进行中/已完成判定）。
 * @param {Object} row LabelTaskPackageRSVO 结构行
 * @param {string} stageCode 角色视角
 */
export function rowState(row, stageCode) {
  const c = row.currentStageCode;
  let done;
  if (stageCode === STAGE_CODES.LABEL) done = !!(c && c !== "LABEL");
  // REVIEW 视角（审核列表）：FBT 值域下 FIRST_REVIEW/SECOND_REVIEW/QC 三格均为"某环节审核进行中"
  // （行=对应环节审核人的操作入口），仅全链 COMPLETED 判已完成——与服务端 stageState 口径一致
  // （stageState=ongoing 收窄 IN(FIRST_REVIEW,SECOND_REVIEW,QC)，finished=COMPLETED）。
  // 原判定照抄 fe 老码字面值（QC/ACCEPTANCE 判已完成）：QC（质检中）被误判已完成，质检人只剩"详情"入口
  else if (stageCode === STAGE_CODES.REVIEW) done = c === PKG_STAGE.COMPLETED;
  else if (stageCode === STAGE_CODES.QC) done = c === PKG_STAGE.COMPLETED || c === "ACCEPTANCE";
  else if (stageCode === STAGE_CODES.ACCEPTANCE) done = c === PKG_STAGE.COMPLETED;
  else done = c === PKG_STAGE.COMPLETED;
  return { done, stateText: done ? "已完成" : "进行中" };
}

/**
 * 标注类型码 → 中文名（对齐 fe projectLabelTypeList）。
 */
export function labelTypeName(code) {
  const hit = LABEL_TYPE_LIST.find((t) => t.value === code);
  return hit ? hit.label : code || "--";
}

/**
 * 由行数据构造 LS 嵌入页地址（对齐 fe buildLink：
 *   {LS_BASE}/projects/{labelStudioProjectId}/data，task/mode/isView/stageRuleCode 由 LabelToolEmbed 拼装）。
 * @param {Object} row 任务包行（labelStudioProjectId）
 */
export function buildLsPageUrl(row) {
  const pid = row && (row.labelStudioProjectId || row.labelProjectGuid);
  if (!pid) return "";
  return LS_BASE_URL + "/projects/" + pid + "/data";
}

/** 行内 LS 任务 id（对齐 fe：labelStudioTaskId || firstLabelStudioTaskId） */
export function rowTaskId(row) {
  return (row && (row.labelStudioTaskId || row.firstLabelStudioTaskId)) || "";
}

// ---- mock 数据（MOCK_ENABLED=true 时返回，结构=09-11 用户提供的真实接口响应）----
// 源数据：label-manager-test.zgsjzc.com /label-task-package/task-page-list
// 审核视角（stageCode=REVIEW）5 条 + 标注视角（stageCode=LABEL）节选（完整 56 条为同构增量，mock 无需全量）
const MOCK_REVIEW_RECORDS = [
  {
    createTime: "2026-08-11 15:11:06", updateTime: null, updateUserName: null,
    guid: "d443d4c009364c129eb4f70d22244c46", tenantGuid: null,
    labelProjectGuid: "df2f1949ba4248578c23c0e83267b139", projectName: "临床数据文本标注",
    labelProjectDataBatchGuid: null, taskPackageCode: null, externalTaskPackageCode: null,
    taskPackageName: "任务包1", labelTypeCode: "TEXT", businessTypeCode: null,
    currentStageCode: "REVIEW", convertCurrentStageCode: null, taskStatusCode: "PENDING",
    rejectedFlag: "N", assigneeGuid: null, assigneeName: null,
    startTime: "2026-08-11 15:20:22", labelFinishTime: "2026-09-10 15:55:44", duration: null,
    itemTotalNum: 10, itemFinishedNum: 10,
    reviewTaskNum: null, reviewFinishTime: null, qcTaskNum: null, qcFinishTime: null,
    acceptanceTaskNum: null, acceptanceFinishTime: null,
    remark: null, orderNum: null, taskFinishTime: null,
    labelStudioTaskId: 2190, firstLabelStudioTaskId: 2190, labelStudioProjectId: 418,
    allowModifyFlag: "Y", stageRuleCode: "ASC", phaseStartTime: "2026-09-10 15:55:44",
    labelTaskPackageStageConfigs: null,
  },
  {
    createTime: "2026-07-03 13:03:52", updateTime: null, updateUserName: null,
    guid: "da3e5dfd8b814f28a0055eae5a8e5661", tenantGuid: null,
    labelProjectGuid: "e54e471157d44b76b3400d862ab7714a", projectName: "图像识别",
    labelProjectDataBatchGuid: null, taskPackageCode: null, externalTaskPackageCode: null,
    taskPackageName: "任务包3", labelTypeCode: "IMAGE", businessTypeCode: null,
    currentStageCode: "ACCEPTANCE", convertCurrentStageCode: null, taskStatusCode: "PENDING",
    rejectedFlag: "Y", assigneeGuid: null, assigneeName: null,
    startTime: "2026-07-03 13:04:17", labelFinishTime: "2026-07-03 14:51:25", duration: null,
    itemTotalNum: 5, itemFinishedNum: 5,
    reviewTaskNum: 5, reviewFinishTime: "2026-07-03 14:51:43", qcTaskNum: 5, qcFinishTime: null,
    acceptanceTaskNum: 0, acceptanceFinishTime: null,
    remark: null, orderNum: null, taskFinishTime: null,
    labelStudioTaskId: null, firstLabelStudioTaskId: 2172, labelStudioProjectId: 411,
    allowModifyFlag: "Y", stageRuleCode: "ASC", phaseStartTime: "2026-07-03 14:51:25",
    labelTaskPackageStageConfigs: null,
  },
  {
    createTime: "2026-07-02 15:51:04", updateTime: null, updateUserName: null,
    guid: "ce19c1e59e774eae8dc291f1e829517d", tenantGuid: null,
    labelProjectGuid: "e54e471157d44b76b3400d862ab7714a", projectName: "图像识别",
    labelProjectDataBatchGuid: null, taskPackageCode: null, externalTaskPackageCode: null,
    taskPackageName: "任务包1", labelTypeCode: "IMAGE", businessTypeCode: null,
    currentStageCode: "ACCEPTANCE", convertCurrentStageCode: null, taskStatusCode: "PENDING",
    rejectedFlag: "N", assigneeGuid: null, assigneeName: null,
    startTime: "2026-07-02 15:53:35", labelFinishTime: "2026-07-02 17:17:11", duration: null,
    itemTotalNum: 5, itemFinishedNum: 5,
    reviewTaskNum: 5, reviewFinishTime: "2026-07-03 14:51:53", qcTaskNum: null, qcFinishTime: null,
    acceptanceTaskNum: null, acceptanceFinishTime: null,
    remark: null, orderNum: null, taskFinishTime: null,
    labelStudioTaskId: null, firstLabelStudioTaskId: 2152, labelStudioProjectId: 406,
    allowModifyFlag: "Y", stageRuleCode: "ASC", phaseStartTime: "2026-07-02 17:17:11",
    labelTaskPackageStageConfigs: null,
  },
  {
    createTime: "2026-07-02 14:48:32", updateTime: null, updateUserName: null,
    guid: "2c9c25ad26e4484795751db9ef65753b", tenantGuid: null,
    labelProjectGuid: "2672174ec123407888fe7b2b04579dfb", projectName: "小票识别",
    labelProjectDataBatchGuid: null, taskPackageCode: null, externalTaskPackageCode: null,
    taskPackageName: "任务包2", labelTypeCode: "IMAGE", businessTypeCode: null,
    currentStageCode: "COMPLETED", convertCurrentStageCode: null, taskStatusCode: "COMPLETED",
    rejectedFlag: "Y", assigneeGuid: null, assigneeName: null,
    startTime: "2026-07-02 14:51:49", labelFinishTime: "2026-07-02 15:46:59", duration: null,
    itemTotalNum: 5, itemFinishedNum: 5,
    reviewTaskNum: 5, reviewFinishTime: "2026-07-02 15:54:06", qcTaskNum: 5, qcFinishTime: null,
    acceptanceTaskNum: 5, acceptanceFinishTime: null,
    remark: null, orderNum: null, taskFinishTime: null,
    labelStudioTaskId: null, firstLabelStudioTaskId: 2147, labelStudioProjectId: 405,
    allowModifyFlag: "Y", stageRuleCode: "ASC", phaseStartTime: "2026-07-02 15:46:59",
    labelTaskPackageStageConfigs: null,
  },
  {
    createTime: "2026-07-02 14:48:32", updateTime: null, updateUserName: null,
    guid: "a0fef0297115408b9b82ff72f2c663c0", tenantGuid: null,
    labelProjectGuid: "2672174ec123407888fe7b2b04579dfb", projectName: "小票识别",
    labelProjectDataBatchGuid: null, taskPackageCode: null, externalTaskPackageCode: null,
    taskPackageName: "任务包1", labelTypeCode: "IMAGE", businessTypeCode: null,
    currentStageCode: "COMPLETED", convertCurrentStageCode: null, taskStatusCode: "COMPLETED",
    rejectedFlag: "Y", assigneeGuid: null, assigneeName: null,
    startTime: "2026-07-02 14:51:49", labelFinishTime: "2026-07-02 15:35:01", duration: null,
    itemTotalNum: 5, itemFinishedNum: 5,
    reviewTaskNum: 5, reviewFinishTime: "2026-07-02 15:35:23", qcTaskNum: 5, qcFinishTime: null,
    acceptanceTaskNum: 5, acceptanceFinishTime: null,
    remark: null, orderNum: null, taskFinishTime: null,
    labelStudioTaskId: null, firstLabelStudioTaskId: 2142, labelStudioProjectId: 404,
    allowModifyFlag: "Y", stageRuleCode: "ASC", phaseStartTime: "2026-07-02 15:35:01",
    labelTaskPackageStageConfigs: null,
  },
];

const MOCK_LABEL_RECORDS = [
  {
    createTime: "2026-08-11 15:11:06", updateTime: null, updateUserName: null,
    guid: "d443d4c009364c129eb4f70d22244c46", tenantGuid: null,
    labelProjectGuid: "df2f1949ba4248578c23c0e83267b139", projectName: "临床数据文本标注",
    labelProjectDataBatchGuid: null, taskPackageCode: null, externalTaskPackageCode: null,
    taskPackageName: "任务包1", labelTypeCode: "TEXT", businessTypeCode: null,
    currentStageCode: "REVIEW", convertCurrentStageCode: null, taskStatusCode: "PENDING",
    rejectedFlag: "N", assigneeGuid: null, assigneeName: null,
    startTime: "2026-08-11 15:20:22", labelFinishTime: "2026-09-10 15:55:44", duration: null,
    itemTotalNum: 10, itemFinishedNum: 10,
    reviewTaskNum: null, reviewFinishTime: null, qcTaskNum: null, qcFinishTime: null,
    acceptanceTaskNum: null, acceptanceFinishTime: null,
    remark: null, orderNum: null, taskFinishTime: null,
    labelStudioTaskId: null, firstLabelStudioTaskId: 2190, labelStudioProjectId: 418,
    allowModifyFlag: "", stageRuleCode: "", phaseStartTime: "2026-08-11 15:11:06",
    labelTaskPackageStageConfigs: null,
  },
  {
    createTime: "2026-08-11 15:11:06", updateTime: null, updateUserName: null,
    guid: "3769850c4d7c4f33b0b2d3793fc0cbc9", tenantGuid: null,
    labelProjectGuid: "df2f1949ba4248578c23c0e83267b139", projectName: "临床数据文本标注",
    labelProjectDataBatchGuid: null, taskPackageCode: null, externalTaskPackageCode: null,
    taskPackageName: "任务包2", labelTypeCode: "TEXT", businessTypeCode: null,
    currentStageCode: "LABEL", convertCurrentStageCode: null, taskStatusCode: "DONE",
    rejectedFlag: "N", assigneeGuid: null, assigneeName: null,
    startTime: "2026-08-11 15:20:22", labelFinishTime: null, duration: null,
    itemTotalNum: 10, itemFinishedNum: 0,
    reviewTaskNum: null, reviewFinishTime: null, qcTaskNum: null, qcFinishTime: null,
    acceptanceTaskNum: null, acceptanceFinishTime: null,
    remark: null, orderNum: null, taskFinishTime: null,
    labelStudioTaskId: 2200, firstLabelStudioTaskId: 2200, labelStudioProjectId: 419,
    allowModifyFlag: "", stageRuleCode: "", phaseStartTime: "2026-08-11 15:11:06",
    labelTaskPackageStageConfigs: null,
  },
  {
    createTime: "2026-08-11 15:11:06", updateTime: null, updateUserName: null,
    guid: "da7709e3d1534875a3dd4dbf6e649c73", tenantGuid: null,
    labelProjectGuid: "df2f1949ba4248578c23c0e83267b139", projectName: "临床数据文本标注",
    labelProjectDataBatchGuid: null, taskPackageCode: null, externalTaskPackageCode: null,
    taskPackageName: "任务包3", labelTypeCode: "TEXT", businessTypeCode: null,
    currentStageCode: "LABEL", convertCurrentStageCode: null, taskStatusCode: "DONE",
    rejectedFlag: "N", assigneeGuid: null, assigneeName: null,
    startTime: "2026-08-11 15:20:22", labelFinishTime: null, duration: null,
    itemTotalNum: 10, itemFinishedNum: 0,
    reviewTaskNum: null, reviewFinishTime: null, qcTaskNum: null, qcFinishTime: null,
    acceptanceTaskNum: null, acceptanceFinishTime: null,
    remark: null, orderNum: null, taskFinishTime: null,
    labelStudioTaskId: 2210, firstLabelStudioTaskId: 2210, labelStudioProjectId: 420,
    allowModifyFlag: "", stageRuleCode: "", phaseStartTime: "2026-08-11 15:11:06",
    labelTaskPackageStageConfigs: null,
  },
];

/**
 * 模拟 task-page-list：按视角取真实样例集，套分页/筛选。
 * @param {Object} body buildTaskPageQuery 产物
 * @returns Promise<{records,totalRows,totalPages,pageIndex,pageSize,totalList}>
 */
export function mockTaskPageList(body) {
  let source;
  if (body.stageCode === STAGE_CODES.REVIEW) source = MOCK_REVIEW_RECORDS;
  else if (body.stageCode === STAGE_CODES.LABEL) source = MOCK_LABEL_RECORDS;
  else source = MOCK_REVIEW_RECORDS.concat(MOCK_LABEL_RECORDS);
  const nameHit = (r) => !body.projectName || r.projectName.indexOf(body.projectName) > -1 || r.taskPackageName.indexOf(body.projectName) > -1;
  const typeHit = (r) => !body.labelTypeCode || r.labelTypeCode === body.labelTypeCode;
  // 状态口径与服务端 stageState 对齐：按视角解释进行中/已完成（待整改视角=未完结/已完结）
  const stageHit = (r) => {
    if (body.currentStageCode) return r.currentStageCode === body.currentStageCode;
    if (!body.stageState) return true;
    if (body.rejectedFlag) {
      return body.stageState === "finished" ? r.currentStageCode === "COMPLETED" : r.currentStageCode !== "COMPLETED";
    }
    const active = body.stageCode === STAGE_CODES.REVIEW ? "REVIEW" : "LABEL";
    return body.stageState === "finished" ? r.currentStageCode !== active : r.currentStageCode === active;
  };
  const rejectHit = (r) => !body.rejectedFlag || r.rejectedFlag === "Y";
  const filtered = source.filter((r) => nameHit(r) && typeHit(r) && stageHit(r) && rejectHit(r));
  const start = (body.pageIndex - 1) * body.pageSize;
  const records = filtered.slice(start, start + body.pageSize);
  return Promise.resolve({
    records,
    totalRows: filtered.length,
    totalPages: Math.ceil(filtered.length / body.pageSize) || 1,
    pageIndex: body.pageIndex,
    pageSize: body.pageSize,
    totalList: null,
  });
}

/**
 * 模拟驳回摘要（对齐 label-review-record 形态）。
 */
export function mockRejectInfo() {
  return Promise.resolve({
    rejectTime: "2026-09-08 14:30:00",
    rejectStaffName: "李审核",
    rejectReason: "省份/城市边界标注不规范，城市实体漏标 12 条，请按标注规范第 3.2 节重新标注。",
    currentItem: "条目 TXT-00234",
    needRectifyCount: 12,
    rectifiedCount: 3,
  });
}

/**
 * 模拟 LS 标注页地址（形态对齐 fe buildLink 项目页）。
 * @param {string|number} projectId LS 项目 id（labelStudioProjectId）
 */
export function mockLabelUrl(projectId) {
  return Promise.resolve({
    url: LS_BASE_URL + "/projects/" + (projectId || 418) + "/data",
    targetRoute: "",
  });
}
